import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Sparkles } from "lucide-react";
import { api, BACKEND_URL } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const MODELS = [
  { id: "claude", label: "Claude Sonnet 5.5 (Anthropic)" },
  { id: "gpt", label: "GPT-5.6 Terra (OpenAI)" },
  { id: "gemini", label: "Gemini 3.1 Pro (Google)" },
];
const selectCls = "h-10 w-full rounded-md border border-input bg-white px-3 text-sm font-medium";

export const parseAi = (txt) => {
  const idx = txt.indexOf("\n---");
  const head = idx >= 0 ? txt.slice(0, idx) : "";
  const body = idx >= 0 ? txt.slice(idx + 4) : txt;
  const get = (k) => head.match(new RegExp(`^\\**${k}\\**:\\s*(.+)$`, "m"))?.[1]?.trim() || "";
  return {
    title: get("TITLE"), slug: get("SLUG"), excerpt: get("EXCERPT"), seo_title: get("SEO_TITLE"), seo_description: get("SEO_DESCRIPTION"),
    keywords: get("KEYWORDS").split(",").map((s) => s.trim()).filter(Boolean), content: body.trim(),
  };
};

const streamBlog = async (body, onText) => {
  const call = () => fetch(`${BACKEND_URL}/api/admin/ai/blog`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
  let res = await call();
  if (res.status === 401) {
    await api.post("/auth/refresh");
    res = await call();
  }
  if (!res.ok) throw new Error((await res.json().catch(() => ({})))?.detail?.[0]?.msg || "Could not start the AI writer");
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = "";
  let full = "";
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    const parts = buf.split("\n\n");
    buf = parts.pop();
    for (const p of parts) {
      if (!p.startsWith("data: ")) continue;
      const ev = JSON.parse(p.slice(6));
      if (ev.error) throw new Error(ev.error);
      if (ev.t) {
        full += ev.t;
        onText(full);
      }
    }
  }
  return full;
};

export const AiWriter = ({ onResult, defaultOpen }) => {
  const [ai, setAi] = useState({ subject: "", keywords: "", model: "claude", length: "medium", notes: "" });
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(!!defaultOpen);
  const set = (k) => (e) => setAi({ ...ai, [k]: e.target.value });

  const generate = async () => {
    if (ai.subject.trim().length < 3) return toast.error("Please enter a subject");
    setBusy(true);
    setDraft("");
    try {
      const full = await streamBlog(ai, setDraft);
      onResult(parseAi(full));
      toast.success("Draft ready — review the fields below, add media and save");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (!open)
    return <button type="button" onClick={() => setOpen(true)} className="btn-outline" data-testid="ai-writer-open"><Sparkles className="h-4 w-4" /> Write / rewrite with AI</button>;

  return (
    <div className="space-y-5 rounded-2xl border-2 border-forest bg-white p-6 sm:p-8" data-testid="ai-writer">
      <div className="flex items-center gap-3"><Sparkles className="h-5 w-5 text-forest" /><h2 className="text-xl font-black text-forest">AI SEO Blog Writer</h2></div>
      <div className="grid gap-5 sm:grid-cols-2">
        <div className="space-y-2 sm:col-span-2"><Label className="font-bold">Subject</Label><Input value={ai.subject} onChange={set("subject")} placeholder="e.g. Best horseback riding tours near San Juan del Sur" data-testid="ai-subject" /></div>
        <div className="space-y-2 sm:col-span-2"><Label className="font-bold">SEO keywords</Label><Input value={ai.keywords} onChange={set("keywords")} placeholder="horse tours, equestrian, playa yankee, nicaragua" data-testid="ai-keywords" /></div>
        <div className="space-y-2">
          <Label className="font-bold">AI model</Label>
          <select className={selectCls} value={ai.model} onChange={set("model")} data-testid="ai-model">{MODELS.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}</select>
        </div>
        <div className="space-y-2">
          <Label className="font-bold">Length</Label>
          <select className={selectCls} value={ai.length} onChange={set("length")} data-testid="ai-length">
            <option value="short">Short (~500 words)</option><option value="medium">Medium (~900 words)</option><option value="long">Long (~1400 words)</option>
          </select>
        </div>
        <div className="space-y-2 sm:col-span-2"><Label className="font-bold">Extra notes (optional)</Label><Textarea rows={2} value={ai.notes} onChange={set("notes")} placeholder="Facts to include, tone, audience…" data-testid="ai-notes" /></div>
      </div>
      <button type="button" onClick={generate} disabled={busy} className="btn-primary" data-testid="ai-generate">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} {busy ? "Writing…" : "Generate blog post"}
      </button>
      {draft && <pre className="max-h-72 overflow-auto whitespace-pre-wrap rounded-xl bg-linen p-4 text-xs leading-relaxed text-ink" data-testid="ai-draft">{draft}</pre>}
    </div>
  );
};
